import { useMemo } from 'react'

import { Hex } from 'viem'

import { useBridgePastStateChanges } from '~/components/txn-display/state-change/hooks/useBridgePastStateChanges'
import { useBridgePendingStateChanges } from '~/components/txn-display/state-change/hooks/useBridgePendingStateChanges'
import { useBuildPendingStateChangeProps } from '~/components/txn-display/state-change/hooks/useBuildPendingStateChangeProps'
import { useCheckIfQuoteExpired } from '~/components/txn-display/state-change/hooks/useCheckIfQuoteExpired'
import { usePastStateChanges } from '~/components/txn-display/state-change/hooks/usePastStateChanges'
import { useRunTenderlySimulation } from '~/components/txn-display/state-change/hooks/useRunTenderlySimulation'
import { useSimulatedStateChanges } from '~/components/txn-display/state-change/hooks/useSimulatedStateChanges'
import {
  DecodedCallSummary,
  ERC20StateChangeItem,
  MetadataTarget,
  StateChangeItem,
} from '~/components/txn-display/state-change/types'
import { buildFallbackStateChangeItems } from '~/components/txn-display/state-change/utils/buildFallbackStateChangeItems'
import { computeUsdValue } from '~/components/txn-display/state-change/utils/buildStateChangeItems'
import { buildWatchAddresses } from '~/components/txn-display/state-change/utils/buildWatchAddresses'
import { getTokenMetadataLookupKey } from '~/components/txn-display/state-change/utils/getTokenMetadataLookupKey'
import { SupportedChainId } from '~/constants/chains'
import { PENDING_TRANSACTION_STATUSES } from '~/constants/smart_accounts'
import {
  BackendTransaction,
  OrgRegularTransaction,
  PublicRegularTransaction,
  TransactionStatus,
} from '~/types'

type RegularTransaction = OrgRegularTransaction | PublicRegularTransaction

export const useTransactionStateChanges = ({
  transaction,
}: {
  transaction: BackendTransaction
}) => {
  const isMerkle = transaction.isMerkleTransaction
  const isPendingTxStatus = PENDING_TRANSACTION_STATUSES.includes(
    transaction.status,
  )
  const isPastTx = !isMerkle && !isPendingTxStatus
  const typedTransaction = isMerkle
    ? undefined
    : (transaction as RegularTransaction)

  // Pending state change hooks
  const {
    transactionId: pendingTransactionId,
    smartAccountAddress: pendingSmartAccountAddress,
    chainId: pendingChainId,
    calls: pendingCalls,
    addressesToWatch: pendingAddressesToWatch,
    simulationUrl,
  } = useBuildPendingStateChangeProps({ transaction: typedTransaction })

  const bridgeStateChanges = useBridgePendingStateChanges(transaction)

  const runTenderlySimulation = useRunTenderlySimulation({
    transaction: typedTransaction,
  })

  const {
    stateChangeItems: pendingStateChangeItems,
    isLoading: isLoadingPendingStateChanges,
    isError: isPendingError,
    errorMessage: simulationErrorMessage,
    isSimulationRevert: pendingSimulationRevert,
    failingCallIndexes,
    decodedCalls,
  } = useSimulatedStateChanges({
    smartAccountAddress: pendingSmartAccountAddress,
    chainId: pendingChainId as SupportedChainId | undefined,
    calls: !isMerkle && isPendingTxStatus ? pendingCalls : [],
    transactionId: pendingTransactionId,
    addressesToWatch:
      !isMerkle && isPendingTxStatus ? pendingAddressesToWatch : [],
    bridgeStateChanges: isPendingTxStatus ? bridgeStateChanges : undefined,
  })

  const { hasError: quoteError, isLoading: isLoadingQuoteExpired } =
    useCheckIfQuoteExpired(isPendingTxStatus ? typedTransaction : undefined)

  // Compute fallback items before usePastStateChanges so their tokens get metadata
  const { fallbackItems, fallbackTokens } = useMemo(() => {
    const items =
      isPastTx &&
      transaction.status === TransactionStatus.Failed &&
      typedTransaction
        ? buildFallbackStateChangeItems(typedTransaction)
        : []

    return {
      fallbackItems: items,
      fallbackTokens: items.map((item): MetadataTarget => ({
        chainId: item.chainId,
        address: item.token,
      })),
    }
  }, [isPastTx, transaction.status, typedTransaction])

  // Past state change hooks
  const {
    stateChangeItems: pastStateChangeItems,
    metadataLookup,
    isLoading: isLoadingPastStateChanges,
    isError: isPastError,
  } = usePastStateChanges({
    transactionHash: isPastTx
      ? (transaction.transactionHash as Hex)
      : undefined,
    chainId: isPastTx ? transaction.chainId : undefined,
    addressesToWatch:
      isPastTx && typedTransaction ? buildWatchAddresses(typedTransaction) : [],
    transactionTime: isPastTx ? transaction.transactionTime : undefined,
    additionalTokens: fallbackTokens,
  })

  const {
    stateChangeItems: bridgePastStateChangeItems,
    isLoading: isLoadingBridgeStateChanges,
    isError: isBridgeError,
  } = useBridgePastStateChanges(isPastTx ? typedTransaction : undefined)

  // Combine past state changes with bridge data
  const pastStateChanges = useMemo<StateChangeItem[]>(() => {
    if (bridgePastStateChangeItems.length > 0) {
      return [...pastStateChangeItems, ...bridgePastStateChangeItems]
    }

    if (bridgeStateChanges && bridgeStateChanges.length > 0) {
      return [
        ...pastStateChangeItems,
        ...bridgeStateChanges.map((change): ERC20StateChangeItem => ({
          id: `bridge-${change.address}-${change.token}-${change.chainId}`,
          state_change_type: 'erc-20',
          address: change.address,
          chainId: change.chainId,
          token: change.token,
          amount: change.amount,
          direction: 'inflow',
        })),
      ]
    }

    // For failed transactions, show the intended transfers from metadata
    if (fallbackItems.length > 0) {
      return fallbackItems.map((item) => {
        const metadata = metadataLookup.get(
          getTokenMetadataLookupKey(item.chainId, item.token),
        )
        return {
          ...item,
          tokenMetadata: metadata,
          usdValue: computeUsdValue(item.amount, metadata),
        }
      })
    }

    return pastStateChangeItems
  }, [
    pastStateChangeItems,
    bridgePastStateChangeItems,
    bridgeStateChanges,
    fallbackItems,
    metadataLookup,
  ])

  // Derive final state
  const stateChangeItems = isPendingTxStatus
    ? pendingStateChangeItems
    : pastStateChanges

  const isLoading = isPendingTxStatus
    ? isLoadingPendingStateChanges || isLoadingQuoteExpired
    : isLoadingPastStateChanges || isLoadingBridgeStateChanges

  const hasError =
    !isLoading &&
    (isPendingTxStatus
      ? isPendingError || quoteError
      : isPastError || isBridgeError)

  const hasNoTransfers =
    !isLoading && !hasError && stateChangeItems.length === 0

  const errorMessage = getErrorMessage({
    hasError,
    isPending: isPendingTxStatus,
    quoteError,
    simulationErrorMessage,
  })
  const isSimulationRevert =
    isPendingTxStatus && !quoteError && !!pendingSimulationRevert

  // Convert stored decodedCalls to DecodedCallSummary format for past transactions
  const pastDecodedCalls = useMemo<DecodedCallSummary[] | undefined>(() => {
    if (isPendingTxStatus || !typedTransaction?.decodedCalls) return undefined

    return typedTransaction.decodedCalls.map(
      (call, index): DecodedCallSummary => ({
        id: `decoded-call-${index}`,
        target: call.to,
        value: BigInt(call.value),
        functionName: call.function,
        args: call.args,
        data: call.data,
      }),
    )
  }, [isPendingTxStatus, typedTransaction?.decodedCalls])

  // Use simulated decodedCalls for pending, stored decodedCalls for past
  const finalDecodedCalls = isPendingTxStatus ? decodedCalls : pastDecodedCalls

  return {
    stateChangeItems,
    isLoading,
    hasError,
    hasNoTransfers,
    errorMessage,
    isSimulationRevert,
    isPending: isPendingTxStatus,
    isMerkle,
    typedTransaction,
    // For simulation button
    runTenderlySimulation,
    simulationUrl,
    // For decoded calldata display
    decodedCalls: finalDecodedCalls,
    // Only surface sim-revert attribution on pending txns; past txns don't simulate.
    failingCallIndexes: isPendingTxStatus ? failingCallIndexes : undefined,
  }
}

export const QUOTE_EXPIRED_ERROR_MESSAGE =
  'The transaction quote is no longer valid. Please cancel and recreate the transaction.'

const getErrorMessage = ({
  hasError,
  isPending,
  quoteError,
  simulationErrorMessage,
}: {
  hasError: boolean
  isPending: boolean
  quoteError: boolean
  simulationErrorMessage?: string
}): string | undefined => {
  if (!hasError) return undefined
  if (!isPending) return 'Error fetching state changes'
  if (quoteError) return QUOTE_EXPIRED_ERROR_MESSAGE
  return simulationErrorMessage ?? 'Error fetching state changes'
}
